import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiSend, FiImage } from "react-icons/fi";
import { FaTimes } from "react-icons/fa";
import moment from "moment";
import { getChatHistoryAPI, addChatMessageAPI, markChatMessageReadAPI } from "../../utils/api";
import "./Chat.css";
import InlineLoader from "../../components/loader/InlineLoader";
import ImageViewer from "../../components/ImageViewer/ImageViewer";
import { BASE_URL, WEB_SOCKET_URL } from "../../utils/configConstants";
import { useSelector } from "react-redux";

const Chat = () => {
  const navigate = useNavigate();
  const {chatUser} = useSelector(state => state.chatState);

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [selectedImages, setSelectedImages] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isImageViewVisible, setIsImageViewVisible] = useState(false);
  const [imageViewImages, setImageViewImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [server, setServer] = useState(null);
  const webSocketRef = useRef(null);
  const [isReconnecting, setIsReconnecting] = useState(false);

  useEffect(() => {
    const getCurrentUser = async () => {
      const userId = await localStorage.getItem('userId');
      setCurrentUserId(userId);
    }
    getCurrentUser();
  }, []);

  // Get chat history using API
  const getChatHistory = async (userId) => {
    try {
      setLoading(true);
      const reqData = {
        user_id: userId,
      };
      const response = await getChatHistoryAPI(reqData);
      if (response?.status === 200) {
        // Sort messages by timestamp to ensure proper order
        const sortedMessages = (response?.data || []).sort((a, b) => 
          new Date(a.createdAt) - new Date(b.createdAt)
        );
        setMessages(sortedMessages);
      } else {
        console.error('Failed to fetch chat history:', response?.message);
      }
    } catch (error) {
      console.error('Error fetching chat history:', error);
    } finally {
      setLoading(false);
    }
  };

  // WebSocket connection function
  const connectWebSocket = () => {
    if (isReconnecting || !currentUserId || !chatUser) {
      return;
    }

    setIsReconnecting(true);
    
    // Close existing connection if any
    if (webSocketRef.current && webSocketRef.current.readyState !== WebSocket.CLOSED) {
      webSocketRef.current.close();
    }

    const ws = new WebSocket(WEB_SOCKET_URL);
    webSocketRef.current = ws;

    ws.onopen = () => {
      console.log('WebSocket connected');
      setServer(ws);
      setIsReconnecting(false);
      ws.send(JSON.stringify({
        type: 'register',
        userId: currentUserId,
        roomId: `room_${chatUser?._id}`,
      }));
    };

    ws.onmessage = (event) => {
      const incomingMessage = JSON.parse(event.data);
      console.log('incomingMessage', incomingMessage);
      if (incomingMessage?.type === 'chat') {
        setMessages((previousMessages) => {
          // Check if message already exists to avoid duplicates
          const messageExists = previousMessages.some(msg => msg._id === incomingMessage._id);
          if (messageExists) {
            return previousMessages;
          }
          // Add new message and sort by timestamp to maintain proper order
          const updatedMessages = [...previousMessages, incomingMessage];
          return updatedMessages.sort((a, b) => new Date(a?.createdAt) - new Date(b?.createdAt));
        });
      }
    };

    ws.onerror = (error) => {
      console.log('websocket error', error);
      setIsReconnecting(false);
    };

    ws.onclose = () => {
      console.log('WebSocket disconnected');
      setServer(null);
      setIsReconnecting(false);
    };
  };

  // Mark messages as read
  const markMessagesAsRead = async (messageId) => {
    try {
      const reqData = {
        room_id: `room_${chatUser?._id}`,
        ...(messageId && { message_id: messageId })
      };
      const response = await markChatMessageReadAPI(reqData);
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  };

  useEffect(() => {
    if (!chatUser) {
      navigate("/chat");
      return;
    }

    if (chatUser && currentUserId) {
      setLoading(true);
      getChatHistory(chatUser?._id);
      
      connectWebSocket();

      markMessagesAsRead();

      return () => {
        if (webSocketRef.current && webSocketRef.current.readyState === WebSocket.OPEN) {
          webSocketRef.current.close();
        }
        webSocketRef.current = null;
      };
    }
  }, [chatUser, currentUserId, navigate]);

  useEffect(() => {
    // Scroll to bottom when messages change
    const scrollToBottom = () => {
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      }
    };
    
    // Small delay to ensure DOM is updated
    const timeoutId = setTimeout(scrollToBottom, 100);
    return () => clearTimeout(timeoutId);
  }, [messages]);

  // Also scroll to bottom when component first loads
  useEffect(() => {
    if (messages.length > 0 && messagesContainerRef.current) {
      const scrollToBottom = () => {
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      };
      const timeoutId = setTimeout(scrollToBottom, 200);
      return () => clearTimeout(timeoutId);
    }
  }, [messages.length]);

  // Image handling functions
  const handleImageSelect = (event) => {
    const files = Array.from(event.target.files);
    const imageFiles = files.filter((file) => file.type.startsWith("image/"));

    if (imageFiles.length > 0) {
      const imagePreviews = imageFiles.map((file) => ({
        file,
        preview: URL.createObjectURL(file),
        name: file.name,
        size: file.size,
      }));
      setSelectedImages((prev) => [...prev, ...imagePreviews]);
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeImage = (index) => {
    setSelectedImages((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      // Revoke object URL to free memory
      URL.revokeObjectURL(prev[index].preview);
      return updated;
    });
  };

  const clearAllImages = () => {
    selectedImages.forEach((img) => URL.revokeObjectURL(img.preview));
    setSelectedImages([]);
  };

  const openImageViewer = (imageUrl) => {
    setImageViewImages([imageUrl]);
    setCurrentImageIndex(0);
    setIsImageViewVisible(true);
  };

  const closeImageViewer = () => {
    setIsImageViewVisible(false);
    setImageViewImages([]);
    setCurrentImageIndex(0);
  };

  const nextImage = () => {
    if (imageViewImages.length > 1) {
      setCurrentImageIndex((prev) => (prev + 1) % imageViewImages.length);
    }
  };

  const previousImage = () => {
    if (imageViewImages.length > 1) {
      setCurrentImageIndex(
        (prev) => (prev - 1 + imageViewImages.length) % imageViewImages.length
      );
    }
  };

  const sendMessage = async () => {
    const hasText = input.trim().length > 0;
    const hasImages = selectedImages.length > 0;

    if (!hasText && !hasImages) return;

    const messageText = input.trim();
    setInput("");

    // Send text message if present
    if (hasText) {
      const date = new Date();
      const messageId = `${currentUserId}${date.getTime().toString()}`;
      
      // Create message object for WebSocket
      const newMessage = {
        type: 'chat',
        _id: messageId,
        createdAt: new Date().toISOString(),
        user: { _id: currentUserId, name: 'Siddhart Fashion' }, // You might want to get actual user name
        sender: currentUserId,
        receive: chatUser?._id,
        text: messageText,
        roomId: `room_${chatUser?._id}`,
      };

      // Send via WebSocket first for real-time delivery
      if (webSocketRef.current && webSocketRef.current.readyState === WebSocket.OPEN) {
        webSocketRef.current.send(JSON.stringify(newMessage));
        setMessages((previousMessages) => {
          const updatedMessages = [...previousMessages, newMessage];
          return updatedMessages.sort((a, b) => new Date(a?.createdAt) - new Date(b?.createdAt));
        });
      } else {
        connectWebSocket();
        setTimeout(() => {
          if (webSocketRef.current && webSocketRef.current.readyState === WebSocket.OPEN) {
            webSocketRef.current.send(JSON.stringify(newMessage));
            setMessages((previousMessages) => {
              const updatedMessages = [...previousMessages, newMessage];
              return updatedMessages.sort((a, b) => new Date(a?.createdAt) - new Date(b?.createdAt));
            });
          }
        }, 1000);
      }

      // Also save to database
      const reqData = new FormData();
      reqData.append('_id', messageId);
      reqData.append('text', messageText);
      reqData.append('createdAt', new Date().toISOString());
      reqData.append('sender', currentUserId);
      reqData.append('receive', chatUser?._id);
      reqData.append('roomId', `room_${chatUser?._id}`);

      try {
        const response = await addChatMessageAPI(reqData);
        if (response?.status === 200) {
          if (response?.data?.message_id) {
            markMessagesAsRead(response?.data?.message_id);
          }
        } else {
          console.error('Failed to save message to database:', response?.message);
        }
      } catch (error) {
        console.error('Error saving message to database:', error);
      }
    }

    // Send images if present
    if (hasImages) {
      setIsUploading(true);
      setUploadProgress(0);

      const totalImages = selectedImages.length;
      let completedImages = 0;

      for (let index = 0; index < selectedImages.length; index++) {
        const imageItem = selectedImages[index];
        try {
          const date = new Date();
          const messageId = `${currentUserId}${index}${date.getTime().toString()}`;
          
          const reqData = new FormData();
          reqData.append('_id', messageId);
          reqData.append('createdAt', new Date().toISOString());
          reqData.append('sender', currentUserId);
          reqData.append('receive', chatUser?._id);
          reqData.append('roomId', `room_${chatUser?._id}`);
          reqData.append("image", imageItem.file);

          const response = await addChatMessageAPI(reqData);
          if (response?.status === 200) {
            // Create message object for WebSocket
            const newMessage = {
              type: 'chat',
              _id: messageId,
              createdAt: new Date().toISOString(),
              user: { _id: currentUserId, name: 'Admin' },
              sender: currentUserId,
              receive: chatUser?._id,
              image: response?.data?.image,
              roomId: `room_${chatUser?._id}`,
            };

            // Send via WebSocket
            if (webSocketRef.current && webSocketRef.current.readyState === WebSocket.OPEN) {
              webSocketRef.current.send(JSON.stringify(newMessage));
            } else {
              connectWebSocket();
              setTimeout(() => {
                if (webSocketRef.current && webSocketRef.current.readyState === WebSocket.OPEN) {
                  webSocketRef.current.send(JSON.stringify(newMessage));
                }
              }, 1000);
            }

            if (response?.data?.message_id) {
              markMessagesAsRead(response?.data?.message_id);
            }
          } else {
            console.error("Image upload failed:", response);
          }
        } catch (error) {
          console.error("Error uploading image:", error);
        } finally {
          completedImages++;
          const progress = (completedImages / totalImages) * 100;
          setUploadProgress(progress);

          if (completedImages === selectedImages.length) {
            // Clear images and refresh chat
            clearAllImages();
            setTimeout(() => {
              getChatHistory(chatUser?._id);
              setIsUploading(false);
              setUploadProgress(0);
            }, 500);
          }
        }
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const renderDateSeparator = useCallback((date) => {
    const messageDate = moment(date);
    const today = moment();
    const yesterday = moment().subtract(1, 'day');
    
    let displayText;
    
    if (messageDate.isSame(today, 'day')) {
      displayText = 'Today';
    } else if (messageDate.isSame(yesterday, 'day')) {
      displayText = 'Yesterday';
    } else {
      displayText = messageDate.format("D MMMM");
    }
    
    return (
      <div key={`date-${date}`} className="date-separator">
        <span className="date-separator-text">{displayText}</span>
      </div>
    );
  }, []);

  const renderMessage = useCallback(
    (item) => {
      const isLoggedInUser = item?.sender == currentUserId;
      const messageTime = moment(item?.createdAt).format("HH:mm");

      // Get full image URL if image exists
      const imageUrl = item?.image
        ? BASE_URL + item?.image
        : null;

      return imageUrl ? (
        <div
          key={item?._id}
          className={`image-message-bubble ${
            isLoggedInUser ? "image-user-bubble" : "image-support-bubble"
          } `}
        >
          <img
            src={imageUrl}
            alt="Message attachment"
            className="message-image"
            onClick={() => openImageViewer(imageUrl)}
            onError={(e) => {
              e.target.style.display = "none";
              console.error("Failed to load image:", imageUrl);
            }}
          />
          <div className="image-message-time">{messageTime}</div>
        </div>
      ) : (
        <div
          key={item?._id}
          className={`message-bubble ${
            isLoggedInUser ? "user-bubble" : "support-bubble"
          } `}
        >
          {item?.text && (
            <>
              <p className="message-text">{item?.text}</p>
              <div className="message-time">{messageTime}</div>
            </>
          )}
        </div>
      );
    },
    [currentUserId]
  );

  const renderMessagesWithDateSeparators = useCallback(() => {
    if (!messages || messages.length === 0) {
      return (
        <div className="empty-messages">
          <p>No messages yet. Start the conversation!</p>
        </div>
      );
    }

    const groupedMessages = [];
    let currentDate = null;

    messages.forEach((message, index) => {
      const messageDate = moment(message?.createdAt).format("YYYY-MM-DD");
      
      // Add date separator if this is a new date
      if (currentDate !== messageDate) {
        groupedMessages.push(renderDateSeparator(message?.createdAt));
        currentDate = messageDate;
      }
      
      // Add the message
      groupedMessages.push(renderMessage(message));
    });

    return groupedMessages;
  }, [messages, renderDateSeparator, renderMessage]);

  return (
    <div className="user-chat-container">
      {loading ? (
        <InlineLoader text="Loading chat..." />
      ) : (
        <>
          {/* Chat Header */}
          <div className="user-chat-header">
            <button
              className="user-chat-back-btn"
              onClick={() => navigate(-1)}
            >
              Back
            </button>
            <div className="user-chat-title">
              <h2>{chatUser?.name || 'Chat'}</h2>
            </div>
          </div>

          {/* Messages Section */}
          <div className="user-chat-messages-section">
            <div className="user-chat-messages-container" ref={messagesContainerRef}>
              {renderMessagesWithDateSeparators()}
              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Input Section */}
          <div className="user-chat-input-section">
            {/* Image Preview Section */}
            {selectedImages.length > 0 && (
              <div className="user-chat-image-preview-section">
                <button
                  className="user-chat-clear-all-images-btn"
                  onClick={clearAllImages}
                >
                  <FaTimes size={15} />
                </button>
                <div className="user-chat-image-preview-container">
                  {selectedImages.map((imageItem, index) => (
                    <div key={index} className="user-chat-image-preview-item">
                      <img
                        src={imageItem.preview}
                        alt={`Preview ${index + 1}`}
                        className="user-chat-image-preview"
                      />
                      <button
                        className="user-chat-remove-image-btn"
                        onClick={() => removeImage(index)}
                      >
                        <FaTimes size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Upload Progress */}
            {isUploading && (
              <div className="user-chat-upload-progress-section">
                <div className="user-chat-upload-progress-bar">
                  <div
                    className="user-chat-upload-progress-fill"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <span className="user-chat-upload-progress-text">
                  Uploading... {Math.round(uploadProgress)}%
                </span>
              </div>
            )}

            {/* Input Container */}
            <div className="user-chat-input-container">
              <button
                className="user-chat-action-btn user-chat-gallery-btn"
                onClick={() => fileInputRef.current?.click()}
                title="Select from Gallery"
              >
                <FiImage size={20} />
              </button>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageSelect}
                style={{ display: "none" }}
              />

              <textarea
                className="user-chat-message-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type your message..."
                onKeyPress={handleKeyPress}
                rows={1}
              />

              <button
                className={`user-chat-send-btn ${
                  input.trim() || selectedImages.length > 0 ? "active" : ""
                }`}
                onClick={sendMessage}
                disabled={!input?.trim() && selectedImages.length === 0}
              >
                <FiSend size={18} />
              </button>
            </div>
          </div>
        </>
      )}

      <ImageViewer
        images={imageViewImages}
        currentIndex={currentImageIndex}
        isVisible={isImageViewVisible}
        onClose={closeImageViewer}
        onNext={nextImage}
        onPrevious={previousImage}
      />
    </div>
  );
};

export default Chat;
